// @flow

import React, { Fragment } from 'react'
import styled, { css } from 'styled-components'
import Colors from 'components/styles/colors'
import { mediaMin } from 'frontend_core/dist/styles/media'
import { fontSize, spacing } from 'frontend_core/dist/styles/variables'
import FormSectionTitle from 'components/molecules/FormSectionTitle'
import HelpTooltip from 'components/molecules/room/HelpTooltip'

const Title = styled.dt`
  margin-bottom: ${spacing.medium}px;
  font-size: ${fontSize.medium}px;
  ${mediaMin.phone`
    margin-bottom: 20px;
`};
  ${props =>
    props.small &&
    css`
      font-size: ${fontSize.small_3}px;
      ${mediaMin.phone`
        font-size: ${fontSize.small_3}px;
        font-weight: bold;
    `};
    `};
`
const RentalType = styled.ul`
  &::after {
    content: '';
    clear: both;
    display: block;
  }
`
const FeeBlock = styled.div`
  margin-top: ${spacing.medium}px;
  line-height: 1.245;
  ${mediaMin.phone`
    margin-top: 0;
  `} &:not(:first-child) {
    ${mediaMin.phone`
      margin-top: ${spacing.medium}px;
    `};
  }

  &::after {
    clear: both;
    content: '';
    display: block;
  }
`
const FeeTitle = styled.dt`
  width: 65%;
  float: left;
  font-size: ${fontSize.small_3}px;
  ${({ isChange }) =>
    isChange &&
    css`
      color: ${Colors.grayB};
      text-decoration: line-through;
      margin-bottom: 6px;
    `} ${({ isAdd }) =>
    isAdd &&
    css`
      &:before {
        content: '+';
        padding-right: ${spacing.tiny}px;
        font-size: 20px;
      }
    `};
  i {
    color: ${Colors.black};
    margin-left: ${spacing.small}px;
  }
`
const FeeDetail = styled.dd`
  width: 35%;
  text-align: right;
  float: right;
  ${({ isDiscount }) =>
    isDiscount &&
    css`
      color: ${Colors.red};
    `} ${({ isChange }) =>
    isChange &&
    css`
      color: ${Colors.grayB};
      text-decoration: line-through;
      margin-bottom: 6px;
    `};
`
const FeeTitleAdd = styled.div`
  width: 65%;
  float: left;
  font-weight: bold;
  i {
    color: ${Colors.black};
    margin-left: ${spacing.small}px;
  }
`
const FeeDetailAdd = styled.div`
  width: 35%;
  text-align: right;
  float: right;
  font-weight: bold;
`
const TotalFeeWrap = styled.div`
  ${mediaMin.phone`
    margin-top: ${spacing.medium}px;
    padding-top: ${spacing.medium}px;
    border-top: 1px dashed ${Colors.grayE};
  `};
`

export type BillItem = {
  name: string,
  price: string,
  changed?: boolean,
  added?: boolean,
  removed?: boolean,
  number?: ?number,
}

function BillItemList(props: {
  billItemList: Array<BillItem>,
  title?: string,
  isDiscount?: boolean,
}) {
  return props.billItemList.map((billItem: BillItem, index: number) => (
    <FeeBlock key={`billItem_${index}`}>
      {billItem.changed && (
        <Fragment>
          <FeeTitle isChange>
            {props.title || billItem.name}
            {billItem.number && `×${billItem.number}個`}
          </FeeTitle>
          <FeeDetail isChange isDiscount={props.isDiscount}>
            {billItem.price}
          </FeeDetail>
        </Fragment>
      )}
      {!billItem.changed &&
        !billItem.added &&
        !billItem.removed && (
          <Fragment>
            <FeeTitle>
              {props.title || billItem.name}
              {billItem.number && `×${billItem.number}個`}
              {props.icon && (
                <a href="#" onClick={props.onClick}>
                  <i className="icon icon-spm-question-circle" />
                </a>
              )}
            </FeeTitle>
            <FeeDetail isDiscount={props.isDiscount}>
              {billItem.price}
            </FeeDetail>
          </Fragment>
        )}
      {billItem.removed && (
        <Fragment>
          <FeeTitle isChange>
            {props.title || billItem.name}
            {billItem.number && `×${billItem.number}個`}
          </FeeTitle>
          <FeeDetail isChange isDiscount={props.isDiscount}>
            {billItem.price}
          </FeeDetail>
        </Fragment>
      )}
      {billItem.added && (
        <FeeBlock>
          <FeeTitle isAdd>
            {props.title || billItem.name}
            {billItem.number && `×${billItem.number}個`}
          </FeeTitle>
          <FeeDetail isDiscount={props.isDiscount}>{billItem.price}</FeeDetail>
        </FeeBlock>
      )}
    </FeeBlock>
  ))
}

export type PropTypes = {
  planBillItemList?: Array<BillItem>,
  extraGuestStayBillItemList?: Array<BillItem>,
  optionCleaningBillItemList?: Array<BillItem>,
  optionBillItemList?: Array<BillItem>,
  changePriceBillItemList?: Array<BillItem>,
  lastMinuteDiscountBillItemList?: Array<BillItem>,
  weeklyDiscountBillItemList?: Array<BillItem>,
  monthlyDiscountBillItemList?: Array<BillItem>,
  guestFeeBillItemList?: Array<BillItem>,
  pointDiscountBillItemList?: Array<BillItem>,
  guestPostpayFeeBillItemList?: Array<BillItem>,
  salesTaxBillItemList?: Array<BillItem>,
  subTotalBillItemList?: Array<BillItem>,
  couponDiscountBillItemList?: Array<BillItem>,
  reservedTotal?: string,
  unapprovedTotal?: string,
  additionalFee?: string,
  additionalPaidyAmount?: string,
}

export default function BillStatement(props: PropTypes) {
  return (
    <Fragment>
      <Title small={props.small}>
        {i18n.t('page.dashboard.reservation.chargeDetails')}
      </Title>
      <dd>
        <dl>
          {props.planBillItemList && (
            <BillItemList billItemList={props.planBillItemList} />
          )}
          {props.extraGuestStayBillItemList && (
            <BillItemList
              billItemList={props.extraGuestStayBillItemList}
              title={i18n.t('page.dashboard.reservation.extraGuestStay')}
            />
          )}
          {props.optionCleaningBillItemList && (
            <BillItemList
              billItemList={props.optionCleaningBillItemList}
              title={i18n.t('page.shared.managementFee')}
            />
          )}
          {props.optionBillItemList && (
            <BillItemList billItemList={props.optionBillItemList} />
          )}
          {props.changePriceBillItemList && (
            <BillItemList billItemList={props.changePriceBillItemList} />
          )}
          {props.lastMinuteDiscountBillItemList && (
            <BillItemList
              billItemList={props.lastMinuteDiscountBillItemList}
              title={i18n.t('page.dashboard.reservation.lastMinutesDiscount')}
              isDiscount
            />
          )}
          {props.weeklyDiscountBillItemList && (
            <BillItemList
              billItemList={props.weeklyDiscountBillItemList}
              title={i18n.t('page.dashboard.reservation.weeklyDiscount')}
              isDiscount
            />
          )}
          {props.monthlyDiscountBillItemList && (
            <BillItemList
              billItemList={props.monthlyDiscountBillItemList}
              title={i18n.t('page.dashboard.reservation.monthlyDiscount')}
              isDiscount
            />
          )}
          {props.guestFeeBillItemList && (
            <BillItemList
              billItemList={props.guestFeeBillItemList}
              title={i18n.t('page.dashboard.reservation.guestFee')}
            />
          )}
          {props.pointDiscountBillItemList && (
            <BillItemList
              billItemList={props.pointDiscountBillItemList}
              title={i18n.t('page.dashboard.reservation.pointDiscount')}
              isDiscount
            />
          )}
          {props.guestPostpayFeeBillItemList && (
            <BillItemList
              billItemList={props.guestPostpayFeeBillItemList}
              title={i18n.t('page.dashboard.reservation.guestPostpayFee')}
            />
          )}
          {props.salesTaxBillItemList && (
            <BillItemList
              billItemList={props.salesTaxBillItemList}
              title={i18n.t('page.dashboard.reservation.salesTax')}
            />
          )}
          {props.couponDiscountBillItemList && (
            <BillItemList
              billItemList={props.couponDiscountBillItemList}
              title={i18n.t('page.dashboard.reservation.couponDiscount')}
              isDiscount
            />
          )}
          <TotalFeeWrap>
            <FeeBlock>
              <FeeTitleAdd>
                {i18n.t('page.dashboard.reservation.totalAfterChange')}
              </FeeTitleAdd>
              <FeeDetailAdd>{props.unapprovedTotal}</FeeDetailAdd>
            </FeeBlock>
            <FeeBlock>
              <FeeTitle>
                {i18n.t('page.dashboard.reservation.totalBeforeChange')}
              </FeeTitle>
              <FeeDetail>{props.reservedTotal}</FeeDetail>
            </FeeBlock>
            {props.additionalFee && (
              <FeeBlock>
                <FeeTitleAdd>
                  {i18n.t('page.dashboard.reservation.addFee')}
                </FeeTitleAdd>
                <FeeDetailAdd>{props.additionalFee}</FeeDetailAdd>
              </FeeBlock>
            )}
            {props.additionalPaidyAmount && (
              <FeeBlock>
                <FeeTitleAdd>
                  {i18n.t(
                    'page.dashboard.reservation.addPaidyAdditionalAmount',
                  )}
                  <span className="mod-evaluate__help">
                    <i className="icon icon-spm-question-circle" />
                    <HelpTooltip
                      text={i18n.t(
                        'page.dashboard.reservation.addPaidyAdditionalAmountTooltipText',
                      )}
                    />
                  </span>
                </FeeTitleAdd>
                <FeeDetailAdd>{props.additionalPaidyAmount}</FeeDetailAdd>
              </FeeBlock>
            )}
          </TotalFeeWrap>
        </dl>
      </dd>
    </Fragment>
  )
}



// WEBPACK FOOTER //
// ./src/components/molecules/BillStatement/index.jsx